import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Badge, Box, Column, DropdownMenu, DropdownMenuItem, IconButton, Row, Typography } from 'seagull';
import { Edit, Email, Kebab, Phone, Trash } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import ClientViewMenuItem from '~/components/ClientViewMenuItem/ClientViewMenuItem';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useIsContactEmailUpdateInProgress } from '~/hooks/usePendingContactEmailUpdates';
import { useLocaleKeys } from '~/i18n';
import { AccountingMappingIndication } from '~/integrations/Accounting/AccountingMappingIndication/AccountingMappingIndication';
import { eligibilityTooltipsTextsPath } from '~/pages/ContactsDashboardOld/ContactsDashboardOld.utils';
import { hasPortalAccess, portalAccessDisabledTooltip } from '~/pages/ContactsDashboardOld/ContactsDashboardRowOld.utils';
import styles from './ContactItemOld.module.scss';
import { deleteTooltipProps } from './ContactItemOld.utils';
export const GetContactItemOldTID = (parentTestId)=>getTestIds(parentTestId);
export function ContactItemOld(props) {
    var _contactModel_customerId, _contactModel_userId;
    const { contact, contactModel, testId, isArchived, onEdit, onDelete } = props;
    const { ContactItemOldTestIds: TID } = useMemo(()=>GetContactItemOldTID(testId), [
        testId
    ]);
    const { t: contactsTexts } = useLocaleKeys('pages.clientDetail.summary.contacts');
    const { t: eligibilityTooltipsTexts } = useLocaleKeys(eligibilityTooltipsTextsPath);
    const { t: clientViewTexts } = useLocaleKeys('components.clientView');
    const { CRM_DRAFT_CONTACTS } = useFeatureFlags();
    const isUpdateInProgress = useIsContactEmailUpdateInProgress(contactModel.userId());
    const reviewerOnlyGate = CRM_DRAFT_CONTACTS === true;
    const showActionsMenu = !contactModel.isSample();
    const deleteDisabled = !contactModel.canDelete();
    const deleteTooltip = deleteTooltipProps(contactModel, eligibilityTooltipsTexts);
    return /*#__PURE__*/ _jsx(Box, {
        testId: testId,
        className: styles.row,
        children: /*#__PURE__*/ _jsxs(Row, {
            gap: 8,
            alignItems: "start",
            justifyContent: "space-between",
            children: [
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 16,
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            gap: 4,
                            alignItems: "center",
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    weight: "normal",
                                    children: contact.name
                                }),
                                contact.mappings.map((mapping)=>/*#__PURE__*/ _jsx(AccountingMappingIndication, {
                                        accounting: mapping.accounting,
                                        display: mapping.display,
                                        purpose: "customer",
                                        size: 16
                                    }, mapping.accounting)),
                                !hasPortalAccess(contactModel, reviewerOnlyGate) && /*#__PURE__*/ _jsx(Badge, {
                                    skin: "default",
                                    size: "small",
                                    rounded: true,
                                    outlined: true,
                                    testId: TID.NoPortalAccess(),
                                    children: contactsTexts.noPortalAccess()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Row, {
                            gap: 8,
                            alignItems: "center",
                            className: styles.contactDetailRow,
                            children: [
                                /*#__PURE__*/ _jsx(Email, {
                                    size: 16
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    weight: "light",
                                    skin: "secondary",
                                    children: contact.email
                                })
                            ]
                        }),
                        contact.phone !== '' && /*#__PURE__*/ _jsxs(Row, {
                            gap: 8,
                            alignItems: "center",
                            testId: TID.Phone(),
                            className: styles.contactDetailRow,
                            children: [
                                /*#__PURE__*/ _jsx(Phone, {
                                    size: 16
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    weight: "light",
                                    skin: "secondary",
                                    children: contact.phone
                                })
                            ]
                        })
                    ]
                }),
                showActionsMenu && /*#__PURE__*/ _jsxs(DropdownMenu, {
                    gap: 0,
                    target: /*#__PURE__*/ _jsx(IconButton, {
                        bi: {
                            name: 'client_detail_contact_menu'
                        },
                        testId: TID.Menu(),
                        skin: "transparent",
                        className: styles.kebab,
                        children: /*#__PURE__*/ _jsx(Kebab, {
                            size: 20
                        })
                    }),
                    children: [
                        !isArchived && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            bi: {
                                name: 'client_detail_contact_edit'
                            },
                            testId: TID.MenuEdit(),
                            iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                            onClick: onEdit,
                            disabled: isUpdateInProgress,
                            tooltipProps: isUpdateInProgress ? {
                                title: contactsTexts.updateInProgress()
                            } : undefined,
                            children: contactsTexts.menu.edit()
                        }),
                        /*#__PURE__*/ _jsx(ClientViewMenuItem, {
                            closeOnClick: true,
                            clientBusinessId: (_contactModel_customerId = contactModel.customerId()) !== null && _contactModel_customerId !== void 0 ? _contactModel_customerId : null,
                            viewUserId: (_contactModel_userId = contactModel.userId()) !== null && _contactModel_userId !== void 0 ? _contactModel_userId : undefined,
                            disabled: !hasPortalAccess(contactModel, reviewerOnlyGate),
                            disabledTooltip: portalAccessDisabledTooltip({
                                contact: contactModel,
                                clientViewTexts,
                                reviewerOnlyGate
                            }),
                            testId: TID.ViewPortal()
                        }),
                        !isArchived && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                            bi: {
                                name: 'client_detail_contact_delete'
                            },
                            testId: TID.MenuDelete(),
                            iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                            skin: "error",
                            onClick: onDelete,
                            disabled: deleteDisabled,
                            tooltipProps: deleteTooltip,
                            children: contactsTexts.menu.delete()
                        })
                    ]
                })
            ]
        })
    });
}
